import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Card,
  Col,
  Collapse,
  Divider,
  Form,
  Input,
  Modal,
  Row,
  Space,
  Table,
  Tag,
  Checkbox,
  Select,
  message,
} from "antd";
import { ColumnsType } from "antd/es/table";
import axios from "axios";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { BASE_API_URL } from "../../../utils/consts";
import { isAdmin, isSuperAdmin } from "../../../utils/permissions";
import { useAuth } from "../../../utils/useAuth";
import { usePermissions } from "../../../utils/usePermissions";

type AccessProfile = {
  _id: string;
  name: string;
  description?: string;
  permissions: string[];
  isSystem?: boolean;
  userCount?: number;
};

type Props = {};

const AccessProfiles: React.FC<Props> = () => {
  const { t } = useTranslation();
  const userData = useSelector((state: any) => state.userData.user);
  const availablePermissions = useSelector(
    (state: any) => state.permissions.availablePermissions
  );
  usePermissions(); // load global permissions if not already loaded
  const authUser: any = useAuth();
  const clientId = userData?.clientId?._id || userData?.clientId;

  const [profiles, setProfiles] = useState<AccessProfile[]>([]);
  const [permissions, setPermissions] = useState<
    { key: string; desc: string }[]
  >([]);
  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [enabledFeatures, setEnabledFeatures] = useState<string[] | undefined>(
    userData?.clientId?.settings?.enabledFeatures
  );

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState<AccessProfile | null>(
    null
  );

  const [form] = Form.useForm();
  const [assignForm] = Form.useForm();

  const canManage =
    isAdmin(authUser?.roles) ||
    isSuperAdmin(authUser?.roles || userData?.roles);

  const loadData = async () => {
    if (!clientId) return;
    try {
      setLoading(true);
      const [permsRes, profilesRes, usersRes, featuresRes] = await Promise.all([
        axios.get(`${BASE_API_URL}/admin/${clientId}/permissions`),
        axios.get(`${BASE_API_URL}/admin/${clientId}/access-profiles`),
        axios.get(`${BASE_API_URL}/${clientId}/users`),
        axios.get(`${BASE_API_URL}/client/${clientId}/features`).catch(() => ({
          data: {
            enabledFeatures: userData?.clientId?.settings?.enabledFeatures,
          },
        })),
      ]);
      setPermissions(permsRes.data || []);
      setProfiles(profilesRes.data || []);
      setUsers(usersRes.data || []);
      setEnabledFeatures(
        featuresRes.data?.enabledFeatures ??
          userData?.clientId?.settings?.enabledFeatures
      );
    } catch (error: any) {
      console.error(error);
      message.error(
        error?.response?.data?.message || t("Failed to load access profiles")
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [clientId]);

  const openCreate = () => {
    setSelectedProfile(null);
    form.resetFields();
    setEditModalOpen(true);
  };

  const openEdit = (profile: AccessProfile) => {
    setSelectedProfile(profile);
    form.setFieldsValue({
      name: profile.name,
      description: profile.description,
      permissions: profile.permissions,
    });
    setEditModalOpen(true);
  };

  const submitProfile = async () => {
    if (!clientId) return;
    try {
      const values = await form.validateFields();
      const payload = {
        name: values.name,
        description: values.description || "",
        permissions: values.permissions || [],
      };
      if (selectedProfile) {
        await axios.put(
          `${BASE_API_URL}/admin/${clientId}/access-profiles/${selectedProfile._id}`,
          payload
        );
        message.success(t("Profile updated"));
      } else {
        await axios.post(
          `${BASE_API_URL}/admin/${clientId}/access-profiles`,
          payload
        );
        message.success(t("Profile created"));
      }
      setEditModalOpen(false);
      loadData();
    } catch (error: any) {
      if (error?.errorFields) return;
      console.error(error);
      message.error(
        error?.response?.data?.message || t("Failed to save access profile")
      );
    }
  };

  const deleteProfile = async (profile: AccessProfile) => {
    if (!clientId) return;
    Modal.confirm({
      title: t("Delete access profile?"),
      okType: "danger",
      onOk: async () => {
        try {
          await axios.delete(
            `${BASE_API_URL}/admin/${clientId}/access-profiles/${profile._id}`
          );
          message.success(t("Profile deleted"));
          loadData();
        } catch (error: any) {
          console.error(error);
          message.error(
            error?.response?.data?.message || t("Failed to delete profile")
          );
        }
      },
    });
  };

  const openAssignModal = (profile: AccessProfile) => {
    setSelectedProfile(profile);
    const preselected = users
      .filter((u) =>
        (u.accessProfiles || []).some(
          (p: any) => p === profile._id || p?._id === profile._id
        )
      )
      .map((u) => u._id);
    assignForm.setFieldsValue({ userIds: preselected });
    setAssignModalOpen(true);
  };

  const submitAssign = async () => {
    if (!clientId || !selectedProfile) return;
    try {
      const values = await assignForm.validateFields();
      await axios.put(
        `${BASE_API_URL}/admin/${clientId}/access-profiles/${selectedProfile._id}/users`,
        { userIds: values.userIds || [] }
      );
      message.success(t("Assignments updated"));
      setAssignModalOpen(false);
      loadData();
    } catch (error: any) {
      if (error?.errorFields) return;
      console.error(error);
      message.error(
        error?.response?.data?.message || t("Failed to update assignments")
      );
    }
  };

  const columns: ColumnsType<AccessProfile> = [
    { title: t("Name"), dataIndex: "name", key: "name" },
    {
      title: t("Description"),
      dataIndex: "description",
      key: "description",
      render: (v) => v || "-",
    },
    {
      title: t("Users"),
      dataIndex: "userCount",
      key: "userCount",
      width: 90,
      render: (v) => v ?? 0,
    },
    {
      title: t("Type"),
      dataIndex: "isSystem",
      key: "isSystem",
      width: 100,
      render: (v) =>
        v ? <Tag color="blue">System</Tag> : <Tag color="green">Client</Tag>,
    },
    {
      title: t("Actions"),
      key: "actions",
      width: 220,
      render: (_, record) => (
        <Space>
          <Button onClick={() => openAssignModal(record)}>
            {t("Assign Users")}
          </Button>
          {canManage && (
            <>
              <Button onClick={() => openEdit(record)}>{t("Edit")}</Button>
              {!record.isSystem && (
                <Button danger onClick={() => deleteProfile(record)}>
                  {t("Delete")}
                </Button>
              )}
            </>
          )}
        </Space>
      ),
    },
  ];

  const permissionOptions = useMemo(() => {
    const list =
      permissions.length > 0
        ? permissions
        : availablePermissions.map((k: string) => ({
            key: k,
            desc: "",
            page: "other",
          }));

    const featureSource =
      enabledFeatures ?? userData?.clientId?.settings?.enabledFeatures;
    const pageToFeature: Record<string, string> = {
      dashboard: "dashboard",
      patients: "patients",
      "patient-profile": "patientProfile",
      appointments: "appointments",
      schedules: "schedules",
      shifts: "shifts",
      staff: "staff",
      hierarchy: "hierarchy",
      "user-profile": "userProfile",
      "user-payments": "userProfile",
      "user-leaves": "userProfile",
      "user-overtimes": "userProfile",
      payments: "payments",
      leaves: "leaves",
      overtimes: "overtime",
      business: "business",
      finance: "finance",
      calendars: "calendar",
      support: "support",
      "settings-general": "settings",
      "settings-locations": "settings",
      "settings-working-pattern": "settings",
      "settings-leaves": "settings",
      "settings-overtimes": "settings",
      "settings-work-sectors": "settings",
      "settings-business-categories": "settings",
      "settings-daily-revenue": "settings",
      "settings-company-contracts": "settings",
      "settings-client-features": "settings",
      "settings-super-admin": "settings",
      "settings-permissions": "settings",
      "client-investments": "client-investments",
      investments: "investments",
      apartments: "apartments",
      suppliers: "suppliers",
      credits: "credits",
      reservations: "reservations",
    };

    const filtered = list.filter((p: any) => {
      if (!featureSource || !featureSource.length) return true;
      // Uvek zadržavamo već izabrane permisije dok se edituje profil,
      // čak i ako nisu deo aktivnih feature flagova.
      const isAlreadySelected = selectedProfile?.permissions?.includes(p.key);
      if (isAlreadySelected) return true;
      const mappedFeature = pageToFeature[p.page];
      // Stranice koje nisu u mapi nisu vezane za neki feature flag
      // (npr. sistemske/interne stranice) — ne filtriramo ih.
      if (!mappedFeature) return true;
      return featureSource.includes(mappedFeature);
    });

    return filtered.map((p: any) => ({
      label: `${p.key} ${p.desc ? `- ${p.desc}` : ""}`,
      value: p.key,
    }));
  }, [
    permissions,
    availablePermissions,
    userData?.roles,
    userData?.clientId?.settings?.enabledFeatures,
    selectedProfile?.permissions,
  ]);

  const groupedPermissions = useMemo(() => {
    const list =
      permissions.length > 0
        ? permissions
        : availablePermissions.map((k: string) => ({
            key: k,
            desc: "",
            page: "other",
          }));
    // filtriramo isto kao permissionOptions
    const featureSource =
      enabledFeatures ?? userData?.clientId?.settings?.enabledFeatures;
    const pageToFeature: Record<string, string> = {
      dashboard: "dashboard",
      patients: "patients",
      "patient-profile": "patientProfile",
      appointments: "appointments",
      schedules: "schedules",
      shifts: "shifts",
      staff: "staff",
      hierarchy: "hierarchy",
      "user-profile": "userProfile",
      "user-payments": "userProfile",
      "user-leaves": "userProfile",
      "user-overtimes": "userProfile",
      payments: "payments",
      leaves: "leaves",
      overtimes: "overtime",
      business: "business",
      finance: "finance",
      calendars: "calendar",
      support: "support",
      "settings-general": "settings",
      "settings-locations": "settings",
      "settings-working-pattern": "settings",
      "settings-leaves": "settings",
      "settings-overtimes": "settings",
      "settings-work-sectors": "settings",
      "settings-business-categories": "settings",
      "settings-daily-revenue": "settings",
      "settings-company-contracts": "settings",
      "settings-client-features": "settings",
      "settings-super-admin": "settings",
      "settings-permissions": "settings",
      "client-investments": "client-investments",
      investments: "investments",
      apartments: "apartments",
      suppliers: "suppliers",
      credits: "credits",
      reservations: "reservations",
    };
    const filtered = list.filter((p: any) => {
      if (!featureSource || !featureSource.length) return true;
      const isAlreadySelected = selectedProfile?.permissions?.includes(p.key);
      if (isAlreadySelected) return true;
      const mappedFeature = pageToFeature[p.page];
      if (!mappedFeature) return true;
      return featureSource.includes(mappedFeature);
    });

    return filtered.reduce(
      (acc: Record<string, { key: string; desc?: string }[]>, p: any) => {
        const page = p.page || "other";
        if (!acc[page]) acc[page] = [];
        acc[page].push({ key: p.key, desc: p.desc });
        return acc;
      },
      {}
    );
  }, [
    permissions,
    availablePermissions,
    userData?.roles,
    userData?.clientId?.settings?.enabledFeatures,
    selectedProfile?.permissions,
  ]);

  return (
    <Card>
      <Row justify="space-between" align="middle">
        <Col>
          <h3>{t("Access Profiles")}</h3>
        </Col>
        <Col>
          {canManage && (
            <Button type="primary" onClick={openCreate}>
              + {t("Create Profile")}
            </Button>
          )}
        </Col>
      </Row>
      <Divider />
      <Table
        rowKey="_id"
        loading={loading}
        columns={columns}
        dataSource={profiles}
      />

      <Modal
        open={editModalOpen}
        title={
          selectedProfile
            ? t("Edit Access Profile")
            : t("Create Access Profile")
        }
        onCancel={() => setEditModalOpen(false)}
        className="standardHeader standardFooter"
        onOk={submitProfile}
        width={700}
        okText={t("Save")}
      >
        <Form layout="vertical" form={form}>
          <Form.Item
            label={t("Name")}
            name="name"
            rules={[{ required: true, message: t("Please enter name") }]}
          >
            <Input />
          </Form.Item>
          <Form.Item label={t("Description")} name="description">
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item label={t("Permissions")} name="permissions">
            {permissionOptions.length ? (
              <Checkbox.Group style={{ width: "100%" }}>
                <Collapse
                  defaultActiveKey={Object.keys(groupedPermissions)}
                  style={{ background: "#fff" }}
                >
                  {Object.entries(groupedPermissions).map(
                    ([page, perms]: any) => (
                      <Collapse.Panel
                        header={<b>{page}</b>}
                        key={page}
                        style={{ background: "#fff" }}
                      >
                        <Space direction="vertical" style={{ width: "100%" }}>
                          {perms?.map((p: any) => (
                            <Checkbox value={p.key} key={p.key}>
                              {p.key} {p.desc ? `- ${p.desc}` : ""}
                            </Checkbox>
                          ))}
                        </Space>
                      </Collapse.Panel>
                    )
                  )}
                </Collapse>
              </Checkbox.Group>
            ) : (
              <div>{t("No permissions available to select")}</div>
            )}
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        open={assignModalOpen}
        title={t("Assign Users")}
        className="standardHeader standardFooter"
        onCancel={() => setAssignModalOpen(false)}
        onOk={submitAssign}
        okText={t("Save")}
        width={600}
      >
        <Form layout="vertical" form={assignForm}>
          <Form.Item label={t("Users")} name="userIds">
            <Select
              mode="multiple"
              showSearch
              allowClear
              optionFilterProp="label"
              placeholder={t("Select users")}
              options={users.map((u) => ({
                value: u._id,
                label:
                  `${u.firstName ?? ""} ${u.lastName ?? ""}`.trim() ||
                  u.email ||
                  u.username,
              }))}
            />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default AccessProfiles;
